import React, { useEffect, useState } from "react";
import { Toaster, toast } from "react-hot-toast";
import "./Herosection.css";
import Swal from "sweetalert2";
import { FaImage, FaVideo, FaPlus, FaTrash, FaCheck, FaTimes, FaSpinner } from "react-icons/fa";
import { ClipLoader } from "react-spinners";

const Herosection = () => {
  const [mediaList, setMediaList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("admintoken");
      if (!token) {
        toast.error("Authentication token not found. Please login again.");
        return;
      }

      const response = await fetch(
        "https://admin.riymay.com/api/v1/admin/hero-section/media",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch media: ${response.statusText}`);
      }

      const data = await response.json();
      setMediaList(data.heroSectionMedia || []);
    } catch (error) {
      console.error("Error fetching media:", error);
      toast.error(`Failed to load hero media: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/webm'];
    if (!validTypes.includes(selectedFile.type)) {
      toast.error("Please select a valid image (JPEG, PNG, GIF, WEBP) or video (MP4, WEBM) file");
      e.target.value = null;
      return;
    }

    // Validate file size (max 10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error("File size should be less than 10MB");
      e.target.value = null;
      return;
    }

    setFile(selectedFile);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setFilePreview({
        url: reader.result,
        type: selectedFile.type
      });
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleCreateMedia = async (e) => {
    e.preventDefault();
    
    if (!file) {
      toast.error("Please select a file to upload");
      return;
    }

    setUploadLoading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const token = localStorage.getItem("admintoken");
      if (!token) {
        toast.error("Authentication token not found. Please login again.");
        return;
      }

      const response = await fetch(
        "https://admin.riymay.com/api/v1/admin/hero-section/media", 
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to upload media");
      }

      const data = await response.json();
      toast.success("Hero media uploaded successfully");
      setShowModal(false);
      setFile(null);
      setFilePreview(null);
      fetchMedia();
    } catch (error) {
      console.error("Error creating media:", error);
      toast.error(error.message || "Failed to upload media");
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to undo this action!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setActionLoading(mediaId);
        try {
          const token = localStorage.getItem("admintoken");
          if (!token) {
            toast.error("Authentication token not found. Please login again.");
            return;
          }

          const response = await fetch(
            "https://admin.riymay.com/api/v1/admin/hero-section/media",
            {
              method: "DELETE",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ mediaId }),
            }
          );
  
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || "Failed to delete media");
          }
  
          const data = await response.json();
          toast.success(data.message || "Media deleted successfully");
          fetchMedia();
        } catch (error) {
          console.error("Error deleting media:", error);
          toast.error(error.message || "Failed to delete media");
        } finally {
          setActionLoading(null);
        }
      }
    });
  };
  
  // Fixed function to properly handle activating a different media
  const handleToggleStatus = async (mediaId, currentStatus) => {
    // If already active and trying to deactivate
    if (currentStatus === "active") {
      updateMediaStatus(mediaId, "active"); // Will deactivate it
      return;
    }
    
    // If trying to activate an inactive media
    const activeMedia = mediaList.find(media => media.activeStatus === "active");
    
    if (activeMedia) {
      // Show confirmation dialog
      Swal.fire({
        title: "Active Media Exists",
        text: "Only one hero media can be active at a time. Do you want to deactivate the current active media and activate this one?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, switch active media",
        cancelButtonText: "Cancel"
      }).then(async (result) => {
        if (result.isConfirmed) {
          // First deactivate the currently active media
          try {
            setActionLoading(mediaId);
            const token = localStorage.getItem("admintoken");
            
            // Step 1: Deactivate the currently active media
            const deactivateResponse = await fetch(
              "https://admin.riymay.com/api/v1/admin/hero-section/media", 
              {
                method: "PUT",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ 
                  mediaId: activeMedia._id, 
                  activeStatus: "inactive" 
                }),
              }
            );
            
            if (!deactivateResponse.ok) {
              const errorData = await deactivateResponse.json();
              throw new Error(errorData.message || "Failed to deactivate current media");
            }
            
            // Step 2: Activate the new media
            const activateResponse = await fetch(
              "https://admin.riymay.com/api/v1/admin/hero-section/media", 
              {
                method: "PUT",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ 
                  mediaId: mediaId, 
                  activeStatus: "active" 
                }),
              }
            );
            
            if (!activateResponse.ok) {
              const errorData = await activateResponse.json();
              throw new Error(errorData.message || "Failed to activate new media");
            }
            
            toast.success("Active media switched successfully");
            fetchMedia();
          } catch (error) {
            console.error("Error switching active media:", error);
            toast.error(error.message || "Failed to switch active media");
          } finally {
            setActionLoading(null);
          }
        }
      });
    } else {
      // No active media exists, simply activate this one
      updateMediaStatus(mediaId, "inactive"); // Will activate it
    }
  };

  const updateMediaStatus = async (mediaId, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    setActionLoading(mediaId);
    
    try {
      const token = localStorage.getItem("admintoken");
      if (!token) {
        toast.error("Authentication token not found. Please login again.");
        return;
      }

      const response = await fetch(
        "https://admin.riymay.com/api/v1/admin/hero-section/media", 
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ mediaId, activeStatus: newStatus }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update status");
      }

      toast.success(`Media ${newStatus === "active" ? "activated" : "deactivated"} successfully`);
      fetchMedia();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(error.message || "Failed to update status");
    } finally {
      setActionLoading(null);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setFile(null);
    setFilePreview(null);
  };

  return (
    <div className="hero-section-container">
      <Toaster position="top-right" />
      
      <div className="hero-section-header">
        <h1>Hero Section Management</h1>
        <button 
          className="create-media-btn" 
          onClick={() => setShowModal(true)}
        >
          <FaPlus /> Add New Hero Media
        </button>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Upload Hero Media</h2>
              <button className="close-modal-btn" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>
            
            <form onSubmit={handleCreateMedia}>
              <div className="file-upload-container">
                <label className="file-upload-label">
                  {filePreview ? (
                    <div className="file-preview">
                      {filePreview.type.startsWith('image') ? (
                        <img src={filePreview.url} alt="Preview" />
                      ) : (
                        <video src={filePreview.url} controls />
                      )}
                    </div>
                  ) : (
                    <div className="upload-placeholder">
                      <FaImage className="upload-icon" />
                      <span>Click or drag to upload image/video</span>
                      <small>Supports: JPG, PNG, GIF, WEBP, MP4, WEBM (Max: 10MB)</small>
                    </div>
                  )}
                  <input 
                    type="file" 
                    onChange={handleFileChange} 
                    accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm"
                    className="file-input" 
                  />
                </label>
              </div>
              
              <div className="modal-actions">
                <button 
                  type="button" 
                  className="cancel-btn" 
                  onClick={closeModal}
                  disabled={uploadLoading}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={!file || uploadLoading}
                >
                  {uploadLoading ? (
                    <>
                      <FaSpinner className="spinner-icon" /> Uploading...
                    </>
                  ) : (
                    "Upload Media"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="loading-container">
          <ClipLoader color="#0056b3" size={50} />
          <p>Loading hero media...</p>
        </div>
      ) : mediaList.length === 0 ? (
        <div className="empty-state">
          <FaImage className="empty-icon" />
          <h3>No Hero Media Found</h3>
          <p>Add your first hero section media by clicking the button above.</p>
        </div>
      ) : (
        <div className="media-grid">
          {mediaList.map((media) => (
            <div key={media._id} className={`media-card ${media.activeStatus === 'active' ? 'active-card' : ''}`}>
              <div className="media-status-badge">
                {media.activeStatus === "active" ? (
                  <span className="active-badge"><FaCheck /> Active</span>
                ) : (
                  <span className="inactive-badge"><FaTimes /> Inactive</span>
                )}
              </div>
              
              <div className="media-preview">
                {media.fileType.startsWith("image") ? (
                  <img 
                    src={`https://riymay.com/assets/${media.fileName}`} 
                    alt={media.fileName} 
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YxZjFmMSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIiBmaWxsPSIjODg4ODg4Ij5JbWFnZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+";
                    }}
                  />
                ) : (
                  <video 
                    controls 
                    src={`https://riymay.com/assets/${media.fileName}`}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.parentNode.innerHTML = '<div class="video-error">Video not available</div>';
                    }}
                  />
                )}
              </div>
              
              <div className="media-info">
                <div className="media-type">
                  {media.fileType.startsWith("image") ? (
                    <><FaImage /> Image</>
                  ) : (
                    <><FaVideo /> Video</>
                  )}
                </div>
                <div className="media-name" title={media.fileName}>
                  {media.fileName.length > 20 ? `${media.fileName.substring(0, 20)}...` : media.fileName}
                </div>
              </div>
              
              <div className="media-actions">
                <button 
                  className="toggle-status-btn"
                  onClick={() => handleToggleStatus(media._id, media.activeStatus)}
                  disabled={actionLoading === media._id}
                >
                  {actionLoading === media._id ? (
                    <FaSpinner className="spinner-icon" />
                  ) : media.activeStatus === "active" ? (
                    "Deactivate"
                  ) : (
                    "Activate"
                  )}
                </button>
                
                <button 
                  className="delete-btn"
                  onClick={() => handleDeleteMedia(media._id)}
                  disabled={actionLoading === media._id}
                >
                  {actionLoading === media._id ? (
                    <FaSpinner className="spinner-icon" />
                  ) : (
                    <><FaTrash /> Delete</>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Herosection;